import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  Code, 
  Smartphone, 
  TrendingUp, 
  Building2, 
  ShoppingCart,
  ArrowRight,
  Award,
  Shield,
  MapPin,
  Plane,
  Receipt,
  Calendar,
  Car,
  Briefcase
} from 'lucide-react';

const Home = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    projects: 0,
    experience: 0,
    clients: 0
  });

  // Counter animation
  useEffect(() => {
    const counters = [
      { key: 'projects', target: 250 },
      { key: 'experience', target: 10 },
      { key: 'clients', target: 100 }
    ];

    counters.forEach(({ key, target }) => {
      let start = 0;
      const increment = target / 50;
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setStats(prev => ({ ...prev, [key]: target }));
          clearInterval(timer);
        } else {
          setStats(prev => ({ ...prev, [key]: Math.floor(start) }));
        }
      }, 50);
    });
  }, []);

  const services = [
    {
      icon: <Code className="w-12 h-12 text-primary" />,
      title: t('services.web_development'),
      description: t('services.web_development_desc'),
      link: '/services/web-development',
      color: 'bg-blue-50'
    },
    {
      icon: <Smartphone className="w-12 h-12 text-primary" />,
      title: t('services.mobile_apps'),
      description: t('services.mobile_apps_desc'),
      link: '/services/mobile-apps',
      color: 'bg-green-50'
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-primary" />,
      title: t('services.digital_marketing'),
      description: t('services.digital_marketing_desc'),
      link: '/services/digital-marketing',
      color: 'bg-purple-50'
    },
    {
      icon: <Plane className="w-12 h-12 text-primary" />,
      title: t('services.travel_portals'),
      description: t('services.travel_portals_desc'),
      link: '/services/travel-portals',
      color: 'bg-cyan-50'
    },
    {
      icon: <Receipt className="w-12 h-12 text-primary" />,
      title: t('services.pos_systems'),
      description: t('services.pos_systems_desc'),
      link: '/services/pos-systems',
      color: 'bg-amber-50'
    },
    {
      icon: <Calendar className="w-12 h-12 text-primary" />,
      title: t('services.booking_systems'),
      description: t('services.booking_systems_desc'),
      link: '/services/booking-systems',
      color: 'bg-rose-50'
    },
    {
      icon: <Car className="w-12 h-12 text-primary" />,
      title: t('services.rental_systems'),
      description: t('services.rental_systems_desc'),
      link: '/services/rental-systems',
      color: 'bg-indigo-50'
    },
    {
      icon: <Building2 className="w-12 h-12 text-primary" />,
      title: t('services.real_estate'),
      description: t('services.real_estate_desc'),
      link: '/services/real-estate',
      color: 'bg-orange-50'
    },
    {
      icon: <Briefcase className="w-12 h-12 text-primary" />,
      title: t('services.company_management'),
      description: t('services.company_management_desc'),
      link: '/services/company-management',
      color: 'bg-teal-50'
    },
    {
      icon: <MapPin className="w-12 h-12 text-primary" />,
      title: t('services.pattaya_solutions'),
      description: t('services.pattaya_solutions_desc'),
      link: '/services/pattaya-solutions',
      color: 'bg-pink-50'
    }
  ];

  // testimonials removed

  return (
    <>
      <Helmet>
        <title>Best Software Development Company in Pattaya Thailand | Web, Mobile App & Digital Marketing</title>
        <meta name="description" content="#1 Software Development Company in Pattaya, Thailand. Expert Website Development, Mobile Apps, Digital Marketing, Travel Portals, POS & Booking Systems. 250+ Projects | Local Office | Free Consultation ☎ +66 96 882 6252" />
        <meta name="keywords" content="software development Pattaya, web development Pattaya Thailand, mobile app developer Pattaya, digital marketing agency Pattaya, website design Pattaya, POS system Pattaya, travel portal Thailand, booking system Pattaya, Pattaya IT company, best software company Pattaya, Chonburi web developer" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="#1 Software Development Company in Pattaya Thailand" />
        <meta property="og:description" content="Professional Website Development, Mobile Apps, Digital Marketing & Business Software. 250+ Projects | Local Pattaya Office | Free Consultation" />
        <meta property="og:url" content="https://karnidigitalsolutions.com" />
        <meta property="og:image" content="https://karnidigitalsolutions.com/og-image.jpg" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="th_TH" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Best Software Development Company in Pattaya Thailand" />
        <meta name="twitter:description" content="Expert Web Development, Mobile Apps & Digital Marketing in Pattaya. 250+ Projects | Free Consultation" />
        
        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="TH-20" />
        <meta name="geo.placename" content="Pattaya" />
        <meta name="geo.position" content="12.927608;100.877083" />
        <meta name="ICBM" content="12.927608, 100.877083" />
        
        <link rel="canonical" href="https://karnidigitalsolutions.com" />
        
        {/* Schema.org Structured Data for Google */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Karni Digital Solutions",
            "description": "Leading software development company in Pattaya, Thailand specializing in web development, mobile apps, and digital marketing",
            "url": "https://karnidigitalsolutions.com",
            "logo": "https://karnidigitalsolutions.com/logo.png",
            "image": "https://karnidigitalsolutions.com/office.jpg",
            "telephone": "+66968826252",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "400/726 Office No. 1/5, Village 12, Nongprue Subdistrict",
              "addressLocality": "Pattaya",
              "addressRegion": "Chonburi",
              "postalCode": "20150",
              "addressCountry": "TH"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "12.927608",
              "longitude": "100.877083"
            },
            "areaServed": ["Pattaya", "Chonburi", "Bangkok", "Thailand"],
            "sameAs": [
              "https://www.facebook.com/karnidigitalsolutions",
              "https://www.linkedin.com/company/karnidigitalsolutions"
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "127"
            },
            "priceRange": "฿฿"
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://karnidigitalsolutions.com",
            "name": "Karni Digital Solutions",
            "image": "https://karnidigitalsolutions.com/office.jpg",
            "priceRange": "฿฿",
            "telephone": "+66968826252",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "400/726 Office No. 1/5, Village 12",
              "addressLocality": "Pattaya",
              "addressRegion": "Chonburi",
              "postalCode": "20150",
              "addressCountry": "Thailand"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "18:00"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="min-h-screen bg-gradient-primary flex items-center relative overflow-hidden pt-20 sm:pt-0">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 border border-gold rounded-full"></div>
          <div className="absolute top-32 right-20 w-16 h-16 bg-gold rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 border border-gold"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-8 sm:py-0">
          <div className="text-center">
            <div className="inline-block bg-gold/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6" data-aos="fade-up">
              <span className="text-gold font-semibold text-sm md:text-base">🏆 #1 Rated Software Company in Pattaya</span>
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Transform Your Business with <span className="text-gold">Expert Software</span> in Pattaya
            </h1>
            <p 
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-3 sm:mb-4 max-w-4xl mx-auto font-light px-4"
              data-aos="fade-up" 
              data-aos-delay="200"
            >
              Complete Digital Solutions: Website Development, Mobile Apps, Digital Marketing & Business Software
            </p>
            <p 
              className="text-sm sm:text-base md:text-lg lg:text-xl text-gold mb-6 sm:mb-8 max-w-3xl mx-auto px-4"
              data-aos="fade-up" 
              data-aos-delay="300"
            >
              ⭐ 250+ Projects | 🏢 Pattaya Office | 💬 Thai & English | 🎯 Results Guaranteed
            </p>
            
            {/* CTA Buttons */}
            <div 
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center mb-8 sm:mb-12 md:mb-16 px-4"
              data-aos="fade-up" 
              data-aos-delay="400"
            >
              <Link to="/contact" className="btn-gold w-full sm:w-auto text-center inline-flex items-center justify-center">
                {t('hero.cta_quote')} <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a 
                href="https://wa.me/66968826252" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 w-full sm:w-auto text-center inline-flex items-center justify-center shadow-md hover:shadow-lg"
              >
                💬 Chat on WhatsApp
              </a>
              <a 
                href="https://line.me/R/ti/p/@323flvcm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 w-full sm:w-auto text-center inline-flex items-center justify-center shadow-md hover:shadow-lg"
              >
                💬 Message on LINE
              </a>
              <a 
                href="tel:+66968826252"
                className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 w-full sm:w-auto text-center inline-flex items-center justify-center"
              >
                📞 Call +66 96 882 6252
              </a>
            </div>

            {/* Stats */}
            <div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-4xl mx-auto px-4"
              data-aos="fade-up" 
              data-aos-delay="600"
            >
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gold mb-1 sm:mb-2">
                  {stats.projects}+
                </div>
                <div className="text-white text-xs sm:text-sm md:text-base">{t('stats.projects')}</div>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gold mb-1 sm:mb-2">
                  {stats.experience}+
                </div>
                <div className="text-white text-xs sm:text-sm md:text-base">{t('stats.experience')}</div>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gold mb-1 sm:mb-2">
                  {stats.clients}+
                </div>
                <div className="text-white text-xs sm:text-sm md:text-base">{t('stats.clients')}</div>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gold mb-1 sm:mb-2">2</div>
                <div className="text-white text-xs sm:text-sm md:text-base">Languages</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 opacity-70">
            <div className="flex items-center space-x-2 sm:space-x-3" data-aos="fade-up">
              <Award className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              <span className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">{t('trust.google_partner')}</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3" data-aos="fade-up" data-aos-delay="100">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              <span className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">{t('trust.ssl_secure')}</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3" data-aos="fade-up" data-aos-delay="200">
              <MapPin className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              <span className="text-sm sm:text-base md:text-lg font-semibold text-gray-800">{t('trust.pattaya_office')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <div className="inline-block bg-primary/10 px-6 py-2 rounded-full mb-4" data-aos="fade-up">
              <span className="text-primary font-semibold">🚀 10+ Premium Services</span>
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 sm:mb-6 px-2"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Complete Digital Solutions for Pattaya & Thailand Businesses
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4" data-aos="fade-up" data-aos-delay="200">
              From hotels and restaurants to travel agencies and retail stores - we build <strong>custom software solutions</strong> that drive real business growth in Pattaya and across Thailand.
            </p>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 mt-4 sm:mt-6 px-4" data-aos="fade-up" data-aos-delay="300">
              <div className="flex items-center gap-1 sm:gap-2 bg-green-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <span className="text-lg sm:text-xl md:text-2xl">✓</span>
                <span className="text-xs sm:text-sm font-semibold text-green-700">Local Pattaya Office</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 bg-blue-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <span className="text-lg sm:text-xl md:text-2xl">✓</span>
                <span className="text-xs sm:text-sm font-semibold text-blue-700">Free Consultation</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 bg-purple-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg">
                <span className="text-lg sm:text-xl md:text-2xl">✓</span>
                <span className="text-xs sm:text-sm font-semibold text-purple-700">Money-Back Guarantee</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className={`card ${service.color} hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer group`}
                data-aos="fade-up" 
                data-aos-delay={index * 50}
              >
                <div className="mb-6 group-hover:scale-110 transition-transform duration-300">{service.icon}</div>
                <h3 className="text-2xl font-bold text-primary mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-primary font-semibold flex items-center group-hover:text-gold transition-colors">
                  Learn More <ArrowRight className="ml-2 w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials removed as requested */}

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-12 max-w-4xl mx-auto">
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4 sm:mb-6"
              data-aos="fade-up"
            >
              🎯 Ready to Grow Your Business in Pattaya?
            </h2>
            <p 
              className="text-base sm:text-lg md:text-xl text-primary-600 mb-6 sm:mb-8 px-2"
              data-aos="fade-up" 
              data-aos-delay="200"
            >
              <strong>Join 250+ successful businesses</strong> in Pattaya and Thailand. Get a FREE consultation and project quote today!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-4 sm:mb-6" data-aos="fade-up" data-aos-delay="400">
              <Link 
                to="/contact"
                className="btn-primary inline-flex items-center justify-center text-base sm:text-lg w-full sm:w-auto"
              >
                {t('hero.cta_quote')} <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a 
                href="https://wa.me/66968826252" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 inline-flex items-center justify-center w-full sm:w-auto shadow-md hover:shadow-lg"
              >
                💬 Chat on WhatsApp
              </a>
              <a 
                href="https://line.me/R/ti/p/@323flvcm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 inline-flex items-center justify-center w-full sm:w-auto shadow-md hover:shadow-lg"
              >
                💬 Message on LINE
              </a>
              <a 
                href="tel:+66968826252"
                className="bg-white hover:bg-gray-100 text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-300 inline-flex items-center justify-center w-full sm:w-auto"
              >
                📞 Call +66 96 882 6252
              </a>
            </div>
            <p className="text-xs sm:text-sm text-primary-500 px-2" data-aos="fade-up" data-aos-delay="500">
              ⚡ Average Response Time: <strong>Under 2 hours</strong> | 💯 100% Satisfaction Guarantee
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
